import { useEffect, useMemo, useRef, useState } from "react";
import { IConversation, IMessage } from "./modal";
import { formatChatSepratorDate, formatMessageTime } from "./JVConversations";
import "./jvchat.css";
import JVMessage from "./JVMessage";
import { useNotification } from "../NotificatonContext";
import { baseUrl, useFetch } from "../../hooks/useFetch";
import { Avatar, Box, CloseButton, createIcon, Flex, Icon, IconButton, Modal, ModalCloseButton, ModalContent, ModalOverlay, Text, Tooltip, useToast } from "@chakra-ui/react";
import { ArrowBackIcon, ArrowForwardIcon, AttachmentIcon } from "@chakra-ui/icons";
import { CustomToast } from "../../components";
import { useAuth } from "../../hooks/useAuth";
import { FaFile, FaFileAlt, FaFileExcel, FaFileImage, FaFilePdf, FaFileWord } from "react-icons/fa";
import { IconType } from "react-icons";
import React from "react";

export default function JVChat({ conversation, myId, onBack }: { conversation: IConversation; myId: number; onBack: () => void }) {
  const { connection, getUserById } = useNotification();
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const [input, setInput] = useState("");
  const [selectedFile, setSelectedFile] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  // const [showUser, setShowUser] = useState(false);
  const [view, setView] = useState<{ name?: string; url?: string } | null>(null);
  const toast = useToast();
  const { addToast } = CustomToast();
  const { fetchApi } = useFetch(addToast);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const [replyTo, setReplyTo] = useState<IMessage | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [pickedMentions, setPickedMentions] = useState<Participant[]>([]);
  const { loginObj } = useAuth();

  useEffect(() => {
    let alive = true;
    setReplyTo(null);
    setPickedMentions([]);
    fetchApi<Participant[]>("Experiment/task-participants", "GET", null, `conversationId=${conversation.id}`).then((res) => {
      if (alive && res) setParticipants(res);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation.id]);

  const mentionCandidates = useMemo(() => {
    if (mentionQuery === null) return [];
    const q = mentionQuery.toLowerCase();
    return participants.filter((p) => p.id !== myId && p.name.toLowerCase().includes(q)).slice(0, 6);
  }, [mentionQuery, participants, myId]);

  const mentionRegex = useMemo(() => {
    const names = participants
      .map((p) => p.name)
      .filter(Boolean)
      .sort((a, b) => b.length - a.length)
      .map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
    return names.length ? new RegExp(`(@(?:${names.join("|")}))`, "g") : null;
  }, [participants]);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setInput(value);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
    const caret = e.target.selectionStart ?? value.length;
    const match = /(^|\s)@([\w.-]*)$/.exec(value.slice(0, caret));
    setMentionQuery(match ? match[2] : null);
    setMentionIndex(0);
  };

  const pickMention = (user: Participant) => {
    const el = inputRef.current;
    if (!el) return;
    const caret = el.selectionStart ?? input.length;
    const before = input.slice(0, caret).replace(/@([\w.-]*)$/, () => `@${user.name} `);
    const after = input.slice(caret);
    setInput(before + after);
    setPickedMentions((prev) => (prev.some((p) => p.id === user.id) ? prev : [...prev, user]));
    setMentionQuery(null);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(before.length, before.length);
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (mentionQuery !== null && mentionCandidates.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setMentionIndex((i) => (i + 1) % mentionCandidates.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setMentionIndex((i) => (i - 1 + mentionCandidates.length) % mentionCandidates.length);
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        pickMention(mentionCandidates[mentionIndex]);
        return;
      }
      if (e.key === "Escape") {
        setMentionQuery(null);
        return;
      }
    }
    if (e.key === "Escape" && replyTo) {
      setReplyTo(null);
      return;
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const jumpToMessage = (id: number) => {
    const el = document.getElementById(`jv-msg-${id}`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.remove("jv-flash");
    void el.offsetWidth; // restart the animation
    el.classList.add("jv-flash");
  };

  const renderMessageText = (message: IMessage, isMine: boolean, isImage: boolean) => {
    const plain = <JVMessage isImage={isImage} isMine={isMine} text={message.message} />;
    if (!message.mentions || !mentionRegex) return plain;
    const parts = message.message.split(mentionRegex);
    if (parts.length === 1) return plain;
    return (
      <Text fontSize="sm" whiteSpace="pre-wrap">
        {parts.map((part, i) =>
          i % 2 === 1 ? (
            <Box as="span" key={i} px="6px" py="1px" mx="1px" borderRadius="md" fontWeight="600" bg={isMine ? "whiteAlpha.300" : "blue.50"} color={isMine ? "white" : "blue.600"}>
              {part}
            </Box>
          ) : (
            part
          ),
        )}
      </Text>
    );
  };

  const ReplyIcon = createIcon({
    displayName: "ReplyIcon",
    viewBox: "0 0 24 24",
    path: <path fill="currentColor" d="M10 9V5l-7 7 7 7v-4.1c5 0 8.5 1.6 11 5.1-1-5-4-10-11-11z" />,
  });

  type Participant = { id: number; name: string };

  // function getDateString(date?: string) {
  //   if (date) {
  //     try {
  //       return format(new Date(date), "MM-dd-yyyy");
  //     } catch {
  //       return "-";
  //     }
  //   } else {
  //     return "-";
  //   }
  // }

  // const reqText = (req && req.userId === conversation.userId)
  //     ? req.eciId ? `<eci>${req.reqId} | ${req.jobTitle} | ${getDateString(req.postedDate)}</eci>`
  //         : req.subId ? `<pip>${req.reqId} | ${req.jobTitle} | ${getDateString(req.postedDate)}</pip>`
  //             : `<req>${req.reqId} | ${req.jobTitle} | ${getDateString(req.postedDate)}</req>`
  //     : "";

  const sendMessage = async () => {
    if (!(input.trim() || selectedFile) || !connection || loading) return;

    setLoading(true);

    try {
      let fileData: any = null;

      if (selectedFile) {
        // const token = localStorage.getItem("token") || "";
        const token = loginObj?.token ?? "";
        console.log("[chat] uploading", selectedFile.name, selectedFile.type, selectedFile.size, "hasToken:", !!token);

        const formData = new FormData();
        formData.append("file", selectedFile);

        const res = await fetch(`${baseUrl}Experiment/upload`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });

        if (!res.ok) {
          const body = await res.text().catch(() => "");
          console.error("[chat] upload failed", res.status, body);
          throw new Error(`Upload failed (${res.status})`);
        }

        fileData = await res.json();
        console.log("[chat] upload result", fileData);
      }

      const text = input.trim();
      const mentionIds = pickedMentions
        .filter((p) => text.includes(`@${p.name}`))
        .map((p) => p.id)
        .join(",");

      await connection.invoke("SendGroupMessage", conversation.taskId, text, fileData?.fileUrl ?? null, fileData?.fileName ?? null, fileData?.contentType ?? null, replyTo?.id ?? null, mentionIds || null);

      setInput("");
      setSelectedFile(null);
      setReplyTo(null);
      setPickedMentions([]);
      if (inputRef.current) inputRef.current.style.height = "auto";
    } catch (err: any) {
      console.error("SendGroupMessage failed:", err);
      console.log("SignalR state:", connection.state);
      toast({ title: "Failed to send message", description: err?.message, status: "error", duration: 4000, isClosable: true, position: "top-right" });
    } finally {
      setLoading(false);
    }
  };

  // async function startNewConversation(userId: number) {
  //   if (loading) return;
  //   setLoading(true);
  //   await startNewConversation(userId);
  //   setLoading(false);
  // }

  useEffect(() => {
    bottomRef.current?.scrollIntoView({
      behavior: "auto",
    });
  }, [conversation.id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({
      behavior: "smooth",
    });
  }, [conversation.messages?.length]);

  const selectFile = async (e: any) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedFile(file);
  };

  function getFileLogo(fileName?: string, fileType?: string, isMine?: boolean) {
    const ext = (fileName?.split(".").pop() || "").toLowerCase();
    const type = (fileType || "").toLowerCase();

    let IconComp: IconType = FaFile;
    let color = "#6b7280";

    if (ext === "pdf" || type.includes("pdf")) {
      IconComp = FaFilePdf;
      color = "#e53935";
    } else if (["xls", "xlsx", "csv"].includes(ext) || type.includes("spreadsheet") || type.includes("excel") || type.includes("csv")) {
      IconComp = FaFileExcel;
      color = "#2e7d32";
    } else if (["doc", "docx"].includes(ext) || type.includes("msword") || type.includes("wordprocessing")) {
      IconComp = FaFileWord;
      color = "#1976d2";
    } else if (["txt", "log", "md"].includes(ext) || type.startsWith("text/")) {
      IconComp = FaFileAlt;
    } else if (type.startsWith("image/") || ["png", "jpg", "jpeg", "gif", "webp"].includes(ext)) {
      IconComp = FaFileImage;
      color = "#8e24aa";
    }

    if (isMine) color = "rgba(255,255,255,0.95)";

    return React.createElement(IconComp as React.ElementType, { size: 18, color });
  }
  return (
    <>
      <div style={styles.chatContainer}>
        <div style={styles.chatHeader}>
          <IconButton aria-label="Back" icon={<ArrowBackIcon />} variant="ghost" size="sm" onClick={onBack} />
          <div style={{ minWidth: 0 }}>
            <div style={{ ...styles.name, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {conversation.projectTaskCode}:-{conversation.taskName || `Task #${conversation.taskId}`}
            </div>
          </div>
        </div>

        <div style={styles.messages}>
          {!conversation.messages ? (
            <div className="text-center">Loading...</div>
          ) : conversation.messages.length === 0 ? (
            <Text textAlign="center" color="gray.500" mt={8} fontSize="sm">
              No chat history yet. Start a conversation!
            </Text>
          ) : null}
          {/* {!conversation.messages && <div className="text-center">Loading...</div>} */}
          {/* {conversation.messages?.map((message, index) => {
            const prevMessage = conversation.messages?.[index - 1];
            const showDaySeparator = !prevMessage || new Date(prevMessage.createdAt).toDateString() !== new Date(message.createdAt).toDateString();

            const isMine = message.senderId === myId;
            const isImage = message.fileUrl && message.fileType?.startsWith("image");

            return (
              <div key={message.id}>
                {showDaySeparator && <div style={styles.daySeparator}>{formatChatSepratorDate(message.createdAt)}</div>}
                <div
                  className="jv-message"
                  style={{
                    ...styles.messageRow,
                    justifyContent: isMine ? "flex-end" : "flex-start",
                  }}
                >
                  {!isMine && <div style={styles.senderAvatar}>{getUserById(message.senderId)?.name?.charAt(0).toUpperCase()}</div>}
                  {isMine && (
                    <div className="jv-message-time" style={{ marginRight: 6 }}>
                      {formatMessageTime(message.createdAt)}
                    </div>
                  )}
                  <div
                    style={{
                      ...styles.messageBubble,
                      backgroundColor: isMine ? "#4e73df" : "#e4e6eb",
                      padding: isImage ? "2px 3px" : "10px 15px",
                      borderRadius: isImage ? 10 : 20,
                      color: isMine ? "white" : "black",
                      overflow: "hidden",
                    }}
                  >
                    {!isMine && <div style={styles.senderName}>{getUserById(message.senderId)?.name}</div>}
                    {message.fileUrl && (
                      <div>
                        {!isMine && <div style={styles.senderAvatar}>{getUserById(message.senderId)?.name?.charAt(0).toUpperCase()}</div>}

                        {!isImage && message.fileUrl && (
                          <a
                            href={`${baseUrl}${message.fileUrl}`}
                            target="_blank"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              color: isMine ? "white" : "#1d9bf0",
                              textDecoration: "none",
                              fontWeight: 500,
                              marginBottom: message.message ? 6 : 0,
                            }}
                          >
                            {getFileLogo(message.fileType, isMine)} {message.fileName}
                          </a>
                        )}
                      </div>
                    )}
                    {message.message && <JVMessage isImage={isImage ? true : false} isMine={isMine} text={message.message} />}
                  </div>
                  {!isMine && (
                    <div className="jv-message-time" style={{ marginLeft: 6 }}>
                      {formatMessageTime(message.createdAt)}
                    </div>
                  )}
                </div>
              </div>
            );
          })} */}

          {conversation.messages?.map((message, index) => {
            const prevMessage = conversation.messages?.[index - 1];
            const showDaySeparator = !prevMessage || new Date(prevMessage.createdAt).toDateString() !== new Date(message.createdAt).toDateString();

            const isMine = message.senderId === myId;
            const isImage = !!(message.fileUrl && message.fileType?.startsWith("image"));
            const repliedTo = message.replyToMessageId ? conversation.messages?.find((m) => m.id === message.replyToMessageId) : undefined;
            const mentionsMe = (message.mentions ?? "").split(",").includes(String(myId));

            const replyButton = (
              <Box className="jv-msg-actions" alignSelf="center" mx="2px">
                <Tooltip label="Reply" hasArrow placement="top">
                  <IconButton
                    aria-label="Reply"
                    icon={<ReplyIcon boxSize="14px" />}
                    size="xs"
                    variant="ghost"
                    borderRadius="full"
                    onClick={() => {
                      setReplyTo(message);
                      inputRef.current?.focus();
                    }}
                  />
                </Tooltip>
              </Box>
            );

            return (
              <div key={message.id} id={`jv-msg-${message.id}`}>
                {showDaySeparator && <div style={styles.daySeparator}>{formatChatSepratorDate(message.createdAt)}</div>}
                <div className="jv-message" style={{ ...styles.messageRow, justifyContent: isMine ? "flex-end" : "flex-start" }}>
                  {!isMine && <div style={styles.senderAvatar}>{getUserById(message.senderId)?.name?.charAt(0).toUpperCase()}</div>}
                  {isMine && replyButton}
                  {isMine && (
                    <div className="jv-message-time" style={{ marginRight: 6 }}>
                      {formatMessageTime(message.createdAt)}
                    </div>
                  )}
                  <div
                    style={{
                      ...styles.messageBubble,
                      backgroundColor: isMine ? "#4e73df" : "#e4e6eb",
                      padding: isImage ? "2px 3px" : "10px 15px",
                      borderRadius: isImage ? 10 : 20,
                      color: isMine ? "white" : "black",
                      overflow: "hidden",
                      boxShadow: mentionsMe && !isMine ? "0 0 0 2px #F6AD55" : undefined,
                    }}
                  >
                    {!isMine && <div style={styles.senderName}>{getUserById(message.senderId)?.name}</div>}

                    {message.replyToMessageId && (
                      <Box onClick={() => repliedTo && jumpToMessage(repliedTo.id)} cursor={repliedTo ? "pointer" : "default"} mb="6px" px="10px" py="6px" borderRadius="8px" borderLeft="3px solid" borderLeftColor={isMine ? "whiteAlpha.800" : "blue.500"} bg={isMine ? "blackAlpha.200" : "blackAlpha.100"}>
                        <Text fontSize="11px" fontWeight="700" color={isMine ? "whiteAlpha.900" : "blue.600"} noOfLines={1}>
                          {repliedTo ? (repliedTo.senderId === myId ? "You" : getUserById(repliedTo.senderId)?.name) : "Original message"}
                        </Text>
                        <Text fontSize="12px" opacity={0.85} noOfLines={2}>
                          {repliedTo ? repliedTo.message || repliedTo.fileName || "Attachment" : "Not available"}
                        </Text>
                      </Box>
                    )}

                    {message.fileUrl && isImage && <img src={`${baseUrl}${message.fileUrl}`} alt={message.fileName || "image"} onClick={() => setView({ name: message.fileName, url: `${baseUrl}${message.fileUrl}` })} style={{ display: "block", maxWidth: "100%", maxHeight: 260, borderRadius: 8, cursor: "pointer", objectFit: "cover" }} />}

                    {message.fileUrl && !isImage && (
                      <a
                        href={`${baseUrl}${message.fileUrl}`}
                        download={message.fileName}
                        onClick={async (e) => {
                          e.preventDefault();

                          try {
                            const response = await fetch(`${baseUrl}${message.fileUrl}`, {
                              headers: {
                                Authorization: `Bearer ${loginObj?.token ?? ""}`,
                              },
                            });

                            if (!response.ok) {
                              throw new Error(`Download failed: ${response.status}`);
                            }

                            const blob = await response.blob();
                            const url = URL.createObjectURL(blob);
                            const link = document.createElement("a");

                            link.href = url;
                            link.download = message.fileName || "download";
                            document.body.appendChild(link);
                            link.click();
                            link.remove();

                            window.setTimeout(() => {
                              URL.revokeObjectURL(url);
                            }, 1000);

                            URL.revokeObjectURL(url);
                          } catch (err) {
                            console.error("Download failed:", err);
                            toast({ title: "Failed to download file", status: "error", duration: 4000, isClosable: true });
                          }
                        }}
                        style={{ display: "flex", alignItems: "center", gap: 6, color: isMine ? "white" : "#1d9bf0", textDecoration: "none", fontWeight: 500, marginBottom: message.message ? 6 : 0 }}
                      >
                        {getFileLogo(message.fileName, message.fileType, isMine)} {message.fileName}
                      </a>
                    )}
                    {message.message && <div style={isImage ? { padding: "6px 12px 4px" } : undefined}>{renderMessageText(message, isMine, isImage)}</div>}
                  </div>
                  {!isMine && (
                    <div className="jv-message-time" style={{ marginLeft: 6 }}>
                      {formatMessageTime(message.createdAt)}
                    </div>
                  )}
                  {!isMine && replyButton}
                </div>
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>

        <div style={styles.inputContainer}>
          <input type="file" id="fileInput" style={{ display: "none" }} onChange={selectFile} />

          <IconButton aria-label="Attach file" icon={<AttachmentIcon />} variant="ghost" borderRadius="full" alignSelf="flex-end" onClick={() => document.getElementById("fileInput")?.click()} />
          {/* 
          <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
            {selectedFile && (
              <div style={styles.filePreview}>
                <div style={styles.fileRemove} onClick={() => setSelectedFile(null)}>
                  ✖
                </div>
                {selectedFile.type.startsWith("image") ? (
                  <img src={URL.createObjectURL(selectedFile)} style={styles.fileImage} />
                ) : (
                  <div style={styles.fileInfo}>
                    {getFileLogo(selectedFile?.type)} {selectedFile.name}
                  </div>
                )}
              </div>
            )}

            <textarea
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                e.target.style.height = "auto";
                e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
              }}
              placeholder="Type a message..."
              style={styles.input}
              rows={1}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
            />
          </div> */}

          <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0, position: "relative" }}>
            {mentionQuery !== null && mentionCandidates.length > 0 && (
              <Box position="absolute" bottom="100%" left={0} right={0} mb="8px" bg="white" border="1px solid" borderColor="gray.200" borderRadius="12px" boxShadow="0 10px 30px rgba(16,28,54,0.14)" overflow="hidden" zIndex={10} role="listbox">
                <Text px="12px" pt="8px" pb="4px" fontSize="11px" fontWeight="600" color="gray.500" textTransform="uppercase" letterSpacing="0.04em">
                  People in this task
                </Text>
                {mentionCandidates.map((u, i) => (
                  <Flex
                    key={u.id}
                    role="option"
                    aria-selected={i === mentionIndex}
                    align="center"
                    gap="10px"
                    px="12px"
                    py="8px"
                    cursor="pointer"
                    bg={i === mentionIndex ? "blue.50" : "transparent"}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      pickMention(u);
                    }}
                    onMouseEnter={() => setMentionIndex(i)}
                  >
                    <Avatar size="xs" name={u.name} bg="#4e73df" color="white" />
                    <Text fontSize="sm" fontWeight="500">
                      {u.name}
                    </Text>
                  </Flex>
                ))}
              </Box>
            )}

            {replyTo && (
              <Flex align="center" gap="10px" mb="6px" px="12px" py="8px" bg="gray.50" borderRadius="12px" borderLeft="3px solid" borderLeftColor="blue.500">
                <Box flex="1" minW={0}>
                  <Text fontSize="12px" fontWeight="700" color="blue.600" noOfLines={1}>
                    Replying to {replyTo.senderId === myId ? "yourself" : getUserById(replyTo.senderId)?.name}
                  </Text>
                  <Text fontSize="12px" color="gray.600" noOfLines={1}>
                    {replyTo.message || replyTo.fileName || "Attachment"}
                  </Text>
                </Box>
                <CloseButton size="sm" onClick={() => setReplyTo(null)} />
              </Flex>
            )}

            {selectedFile && (
              <div style={styles.filePreview}>
                <div style={styles.fileRemove} onClick={() => setSelectedFile(null)}>
                  ✖
                </div>
                {selectedFile.type.startsWith("image") ? (
                  // eslint-disable-next-line jsx-a11y/alt-text
                  <img src={URL.createObjectURL(selectedFile)} style={styles.fileImage} />
                ) : (
                  <div style={styles.fileInfo}>
                    {getFileLogo(selectedFile?.name, selectedFile?.type)} {selectedFile.name}
                  </div>
                )}
              </div>
            )}

            <textarea ref={inputRef} value={input} onChange={handleInputChange} onKeyDown={handleKeyDown} placeholder="Type a message... use @ to mention" style={styles.input} rows={1} />
          </div>

          <IconButton aria-label="Send" icon={<ArrowForwardIcon />} colorScheme="blue" borderRadius="full" alignSelf="flex-end" ml={2} isLoading={loading} isDisabled={!input.trim() && !selectedFile} onClick={sendMessage} />
        </div>
      </div>

      <Modal isOpen={!!view} onClose={() => setView(null)} size="4xl" isCentered>
        <ModalOverlay bg="blackAlpha.800" />
        <ModalContent bg="transparent" boxShadow="none">
          <ModalCloseButton color="white" />
          {view?.url && <img src={view.url} alt={view.name} style={{ maxHeight: "80vh", maxWidth: "100%", objectFit: "contain", margin: "0 auto" }} />}
        </ModalContent>
      </Modal>
    </>
  );
}

const styles: Record<string, React.CSSProperties> = {
  chatContainer: { display: "flex", flexDirection: "column", height: "100%", minHeight: 0 },
  chatHeader: { padding: "15px 20px", borderBottom: "1px solid #ddd", display: "flex", alignItems: "center", gap: 10, backgroundColor: "white" },
  avatar: { width: 45, height: 45, borderRadius: "50%", backgroundColor: "#4e73df", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 600, position: "relative" },
  senderAvatar: { width: 32, height: 32, borderRadius: "50%", backgroundColor: "#4e73df", marginRight: 6, color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 600, position: "relative" },
  senderName: { color: "#4e73df", fontSize: 12, fontWeight: "bold" },
  groupAvatar: { width: 45, height: 45, borderRadius: "50%", backgroundColor: "#28c76f", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 600, position: "relative" },
  avatarImg: { width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover", cursor: "pointer" },
  name: { fontWeight: 600 },
  email: { fontWeight: 600, fontSize: 12 },
  onlineDot: { position: "absolute", bottom: 2, right: 2, width: 10, height: 10, borderRadius: "50%", backgroundColor: "#22c55e", border: "2px solid white" },
  messages: { flex: 1, minHeight: 0, overflowY: "auto", padding: 20, backgroundColor: "#f5f7fb" },
  messageRow: { display: "flex", marginBottom: 10 },
  messageBubble: { maxWidth: "60%", wordBreak: "break-word", overflowWrap: "break-word", whiteSpace: "pre-wrap" },
  daySeparator: { textAlign: "center", margin: "12px 0", fontSize: 12, color: "#666", background: "#e5e7eb", padding: "4px 10px", borderRadius: 12, width: "fit-content", marginLeft: "auto", marginRight: "auto" },
  inputContainer: { display: "flex", alignItems: "flex-end", gap: 6, padding: 12, borderTop: "1px solid #e5e7eb", backgroundColor: "white" },
  input: { flex: 1, padding: "10px 14px", borderRadius: 20, border: "1px solid #d1d5db", outline: "none", width: "100%", resize: "none", maxHeight: 120, overflowY: "auto", fontSize: 14 },
  sendBtn: { marginLeft: 10, backgroundColor: "#4e73df", border: "none" },
  filePreview: { position: "relative", backgroundColor: "#f1f5f9", borderRadius: 12, padding: "8px 12px", marginBottom: 6, display: "inline-flex", alignItems: "center" },
  fileRemove: { position: "absolute", top: -6, right: -6, width: 20, height: 20, borderRadius: "50%", backgroundColor: "#ef4444", color: "white", fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" },
  fileImage: { maxWidth: 150, maxHeight: 120, borderRadius: 10, objectFit: "cover" },
  fileInfo: { fontSize: 13, fontWeight: 500, color: "#333", wordBreak: "break-word" },
};
